import { memo, ReactNode, useEffect, useMemo, useState } from 'react';
import { CheckboxButton } from 'src/modules/common/components/Checkbox/CheckboxButton.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { AttributeUniqValues } from 'src/generated/operations.ts';
import { MinusIcon } from 'src/modules/common/icons/MinusIcon';
import { PlusIcon } from 'src/modules/common/icons/PlusIcon';
import clsx from 'clsx';
import { useDebouncedCallback } from 'use-debounce';
import { CheckboxHandler } from 'src/modules/common/types/CheckboxHandler';
import { Filter } from 'src/utils/composeFilter';
import { Comparator } from 'src/common/types/Comparator.ts';
import { AttributeAlias, AttributesAliases } from 'src/generated/dict.ts';

type Props = {
    filter: Filter;
    name: string;
    comparator: Comparator;
    options: AttributeUniqValues['values'];
    disabled?: boolean;
    onChange: CheckboxHandler;
    setFocusedFilterName: (name: string | null) => void;
    subHeader?: ReactNode;
}

const VIEW_MORE_LIMIT = 4;

export const FilterValuesGroup = memo(({
    filter,
    name,
    options,
    disabled,
    comparator,
    onChange,
    setFocusedFilterName,
    subHeader,
}: Props) => {
    const [isFilterCollapsed, setFilterCollapsed] = useState(false);
    const [optionsOpen, setOptionsOpen] = useState(false);

    const selected = useMemo(() => (filter ? filter[name] || [] : []), [filter, name]);

    const [filterValue, setFilterValue] = useState(selected);

    useEffect(() => {
        setFilterValue(selected);
    }, [selected]);

    const debouncedCheckboxHandler = useDebouncedCallback((detail: {
        name: string;
        value: string[];
    }) => {
        const diff1 = detail.value.filter((value) => !selected.includes(value));
        const diff2 = selected.filter((value) => !detail.value.includes(value));

        onChange({ name: detail.name, value: [...diff1, ...diff2] });
    }, 500);

    const updateFilterValue = (newValue: string) => {
        const newFilterValue = filterValue.includes(newValue)
            ? filterValue.filter((prevValue) => prevValue !== newValue)
            : [...filterValue, newValue];

        setFocusedFilterName(name);
        setFilterValue(newFilterValue);
        debouncedCheckboxHandler({ name, value: newFilterValue });
    };

    return (
        <div className={clsx({
            'vss-filter-values-group': true,
            'vss-filter-values-group--disabled': disabled,
        })}
        >
            <div
                className="vss-filter-values-group__header"
                onClick={() => setFilterCollapsed((prev) => !prev)}
                onKeyDown={() => null}
                tabIndex={0}
                role="button"
            >
                <p className="vss-filter-values-group__title"><I18n
                    messageId={AttributesAliases[name as AttributeAlias]}
                />
                </p>

                <div className="vss-filter-values-group__icon">
                    {isFilterCollapsed ? <PlusIcon/> : <MinusIcon/>}
                </div>
            </div>

            {!isFilterCollapsed && (
                <>
                    {subHeader !== undefined && subHeader}
                    {options.length > 0 && (
                        <div className="vss-filter-values-group__options">
                            {options
                                .slice()
                                .sort(comparator)
                                .map(({ value, label, count }, index) => {
                                    const checked = selected.includes(value);
                                    if (checked || optionsOpen || (!optionsOpen && index < VIEW_MORE_LIMIT)) {
                                        return (
                                            <CheckboxButton
                                                key={`${name}_${value}`}
                                                id={`${name}_${value}`}
                                                checked={filterValue.includes(value)}
                                                onChange={() => updateFilterValue(value)}
                                            >
                                                {
                                                    label.startsWith('@')
                                                        ? <I18n messageId={label.slice(1)}/>
                                                        : label
                                                } ({count})
                                            </CheckboxButton>
                                        );
                                    }
                                    return null;
                                })}
                        </div>
                    )}
                    {options.length > VIEW_MORE_LIMIT && (
                        <div className="vss-filter-values-group__actions">
                            <button
                                type="button"
                                className="vss-filter-values-group__action"
                                onClick={() => setOptionsOpen((prev) => !prev)}
                            >
                                <I18n messageId={optionsOpen ? MESSAGES.VIEW_LESS : MESSAGES.VIEW_MORE}/>
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
});
